import type { Currencies, UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { qualifier } from "$root/utils";
import { Button, Radio, RadioGroup, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	type OrderBy,
	type TableColumn,
	BaseTable,
	Controller,
	DebouncedSearchInput,
	Dialog,
	DialogFooter,
	sortRows,
	Text,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { type MultiSelectCtx, useMultiSelect, useSearchable } from "@mdotm/shadcn/legacy/headless";
import type { MaybePromise } from "@mdotm/utils/promise";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSort } from "@mdotm/shadcn/legacy/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClassificationOptionsTable } from "./spawn-tag-classifications";
import { type IdentifiedClassification, cleanClassificationId, userClassificationId } from "./classification-id";

const CurrencyConstraintType = {
	tag: "TAG",
	riskModel: "RISK_MODEL",
} as const;

type CurrencyConstraintType = (typeof CurrencyConstraintType)[keyof typeof CurrencyConstraintType];

type RiskModelCurrency = {
	type: "RISK_MODEL";
	selectedOptions: Array<Currencies>;
};

type TagCurrency = {
	type: "TAG";
	selectedOptions: Array<string /** option */>;
	classification: IdentifiedClassification;
};

type TagCurrencyDraft = {
	type: "TAG";
	classification?: UserInstrumentClassificationDto;
};

type CurrencyConstraintSubmitProps = RiskModelCurrency | TagCurrency;

type CurrencyConstraintDialogProps = MinimumDialogProps & SpawnCurrencyConstraintDialogProps;

function CurrencyConstraintDialog({
	show,
	onClose,
	onAnimationStateChange,
	onSubmit,
	optionsProvider,
	selectableCurrencies,
	selectableUserClassifications,
	disabledRowIds,
	classificationType,
	initialValue,
}: CurrencyConstraintDialogProps): React.JSX.Element {
	const { t } = useTranslation();

	const selectableCurrencyOptions = useMemo(
		() =>
			selectableUserClassifications.filter(
				(classification) => classification.tagType === "CURRENCY" && classification.fieldType === "TAG",
			),
		[selectableUserClassifications],
	);

	const [selectableTag, setSelectableTag] = useState<string[] | undefined>(undefined);
	const [currencyType, setCurrencyType] = useState<Omit<RiskModelCurrency, "selectedOptions"> | TagCurrencyDraft>(
		classificationType === "RISK_MODEL"
			? { type: "RISK_MODEL" }
			: {
					type: "TAG",
					classification: selectableCurrencyOptions.find(
						(classification) => initialValue?.classificationUuid === classification.classificationUuid,
					),
				},
	);

	const tagMultiSelectCtx = useMultiSelect<string>({
		selectable: selectableTag,
	});

	const currencyMultiSelectCtx = useMultiSelect<Currencies>({
		selectable: selectableCurrencies.filter((id) => !disabledRowIds?.includes(id)),
	});

	useEffect(() => {
		if (classificationType === "RISK_MODEL" && initialValue?.selectedOptions && show) {
			currencyMultiSelectCtx.setSelection(initialValue.selectedOptions);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue?.selectedOptions, show]);

	useEffect(() => {
		if (classificationType === "TAG" && initialValue?.selectedOptions && initialValue?.classificationUuid && show) {
			tagMultiSelectCtx.setSelection(
				initialValue.selectedOptions.map((tag) => userClassificationId(initialValue.classificationUuid, tag)),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue?.selectedOptions, show]);

	return (
		<Dialog
			size="xxlarge"
			show={show}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			header="Select"
			onSubmitAsync={() => {
				if (currencyType.type === "RISK_MODEL") {
					onSubmit({
						type: currencyType.type,
						selectedOptions: currencyMultiSelectCtx.selection.valueSeq().toArray(),
					});
					return;
				}

				const classificationUuid = currencyType.classification?.classificationUuid;
				if (!classificationUuid) {
					return;
				}
				const selectedOptions = tagMultiSelectCtx.selection
					.valueSeq()
					.toArray()
					.filter((s) => s.includes(classificationUuid))
					.map((selectionId) => cleanClassificationId(selectionId, classificationUuid));

				onSubmit({
					type: currencyType.type,
					classification: { ...currencyType.classification, classificationUuid },
					selectedOptions,
				});
			}}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton
							palette="primary"
							disabled={
								currencyType.type === "TAG" && !currencyType.classification?.classificationUuid ? true : undefined
							}
							data-qualifier={qualifier.component.button("Dialog/Submit")}
						>
							{t("BUTTON.DONE")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier={qualifier.component.button("Dialog/Cancel")}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<Text type="Body/M/Medium" className="mb-1" as="p">
				Apply constraint on
			</Text>
			<RadioGroup
				onChange={(newAllocationType) => {
					if (newAllocationType === "TAG") {
						const firstClassification = selectableCurrencyOptions.at(0) ?? {};
						setCurrencyType({ type: "TAG", classification: firstClassification });
						tagMultiSelectCtx.reset();
						return;
					}

					setCurrencyType({ type: "RISK_MODEL" });
					currencyMultiSelectCtx.reset();
				}}
				value={currencyType.type}
				className="mb-2"
			>
				<div className="flex flex-row flex-wrap gap-4">
					<Radio
						data-qualifier={`CurrencyConstraintDialog/ApplyConstraintOn/${CurrencyConstraintType.riskModel}`}
						value={CurrencyConstraintType.riskModel}
					>
						Risk Model
					</Radio>
					<Radio
						data-qualifier={`CurrencyConstraintDialog/ApplyConstraintOn/${CurrencyConstraintType.tag}`}
						value={CurrencyConstraintType.tag}
					>
						Currency Tags
					</Radio>
				</div>
			</RadioGroup>
			<Text type="Body/M/Book" className="mb-4" as="p">
				{currencyType.type === "RISK_MODEL"
					? "Choose to apply constraints using Sphere's Risk Model classifications."
					: "Choose to apply constraints using user-defined custom categories."}
			</Text>
			<div className="max-h-[400px] h-[400px] min-h-0 overflow-hidden flex flex-col">
				{currencyType.type === "TAG" ? (
					<ClassificationOptionsTable
						selectedClassificationUuid={currencyType.classification?.classificationUuid}
						mode="radio"
						optionsProvider={optionsProvider}
						selectableUserClassifications={selectableCurrencyOptions}
						onChangeClassification={(newClassification) => {
							if (newClassification) {
								setCurrencyType(() => ({
									classification: newClassification,
									type: "TAG",
								}));
							}
						}}
						onClassificationLoad={(availableOptions) => {
							if (availableOptions && availableOptions.length > 0) {
								const opts = availableOptions.map((x) =>
									userClassificationId(currencyType.classification!.classificationUuid!, x),
								);
								const filteredOpts =
									disabledRowIds && disabledRowIds.length > 0 ? opts.filter((x) => !disabledRowIds.includes(x)) : opts;
								setSelectableTag(filteredOpts);
								return;
							}
							setSelectableTag(undefined);
						}}
						multiSelectCtx={tagMultiSelectCtx}
					/>
				) : currencyType.type === "RISK_MODEL" ? (
					<CurrencyTable
						mode="radio"
						selectableCurrencies={selectableCurrencies}
						multiSelectCtx={currencyMultiSelectCtx}
					/>
				) : null}
			</div>
		</Dialog>
	);
}

type CurrencyTableProps = {
	mode?: "checkbox" | "radio";
	selectableCurrencies: Array<Currencies>;
	multiSelectCtx: MultiSelectCtx<Currencies>;
};

function CurrencyTable({ selectableCurrencies, mode, multiSelectCtx }: CurrencyTableProps) {
	const [search, setSearch] = useState("");

	const { filtered } = useSearchable({
		collection: selectableCurrencies,
		query: search,
		extractFn: (currency) => currency,
	});

	const checkboxColumnData = useSelectableTableColumn({
		mode,
		rows: selectableCurrencies,
		selectBy: (r) => r ?? "",
		filteredRows: filtered,
		multiSelectCtx,
	});

	const columns = useMemo<TableColumn<Currencies>[]>(
		() => [
			checkboxColumnData.column,
			{
				header: "",
				content: (x) => x ?? "",
				sortFn: builtInSort,
				name: "currencyName",
			},
		],
		[checkboxColumnData.column],
	);

	return (
		<>
			<DebouncedSearchInput
				data-qualifier="CurrencyTable/Search"
				externalQuery={search}
				onChange={setSearch}
				placeholder="Search currency.."
				className="mb-4"
			/>

			<Controller value={defaultCurrencyOrderBy}>
				{({ value: orderBy, onChange: onOrderByChange }) => (
					<BaseTable
						columns={columns}
						rows={search ? filtered : sortRows({ rows: filtered, columns, orderByArr: orderBy })}
						orderBy={search ? undefined : orderBy}
						onOrderByChange={onOrderByChange}
						onRowClick={(currency) => {
							if (!checkboxColumnData.multiSelectCtx.disabled(currency)) {
								checkboxColumnData.toggle(currency);
							}
						}}
						className="max-h-[462px] min-h-[320px]"
					/>
				)}
			</Controller>
		</>
	);
}

const defaultCurrencyOrderBy: Array<OrderBy<"currencyName">> = [{ columnName: "currencyName", direction: "asc" }];

type SpawnCurrencyConstraintDialogProps = {
	selectableUserClassifications: UserInstrumentClassificationDto[];
	selectableCurrencies: Array<Currencies>;
	onSubmit(props: CurrencyConstraintSubmitProps): void;
	optionsProvider(classificationUuid: string): MaybePromise<Array<string>>;
	disabledRowIds?: string[];
} & (
	| {
			classificationType: Extract<CurrencyConstraintType, "TAG">;
			initialValue?: {
				selectedOptions: Array<string>;
				classificationUuid: string;
			};
	  }
	| {
			classificationType: Extract<CurrencyConstraintType, "RISK_MODEL">;
			initialValue?: {
				selectedOptions?: Array<Currencies>;
			};
	  }
);

export function spawnCurrencyConstraintDialog(props: SpawnCurrencyConstraintDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<CurrencyConstraintDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmit={(payload) => {
					props.onSubmit(payload);
					resolve();
				}}
			/>
		)),
	);
}
